import { useState } from 'react';
import { AttachCircle, Send, Happyemoji } from 'iconsax-react';
import MessageInput from './atoms/MessageInput';
import ImageUploadInput from './atoms/ImageUploadInput';
import FileUploadInput from './atoms/FileUploadInput';
import ImagePreview from './atoms/ImagePreview';
import FilePreview from './atoms/FilePreview';
import { Popover } from 'antd';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { UploadedFile, UploadedImage } from '../../../types/chat';
import { useChat } from '../../../store/slices/chat';
import { socket } from '../../../lib/socket';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

interface Emoji {
  id: string;
  name: string;
  native: string;
  unified: string;
  keywords: string[];
  shortcodes: string;
  emoticons: string[];
}

interface ChatFooterProps {
  canChat?: boolean;
  roomId?: string;
}

const ChatFooter = ({ canChat = true, roomId: propRoomId }: ChatFooterProps) => {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('');
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedFilesIds, setUploadedFilesIds] = useState<string[]>([]);
  const [uploadedImagesIds, setUploadedImagesIds] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { selectedRoomId, isClosed } = useChat();
  const activeRoomId = propRoomId || selectedRoomId || searchParams.get('chatRoomId') || '';
  const [inputLoading, setInputLoading] = useState(false);
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;

  // Empty functions for ID change handlers since we're not using them right now
  const handleFileIdsChange = (id: string) => {
    setUploadedFilesIds((prev) => [...prev, id]);
  };
  const handleImageIdsChange = (id: string) => {
    setUploadedImagesIds((prev) => [...prev, id]);
  };

  const handleSend = () => {
    if (isClosed) {
      return;
    }
    if (
      !message.trim() &&
      uploadedImages.length === 0 &&
      uploadedFiles.length === 0
    )
      return;
    socket.emit('user-messages', {
      senderId: userId,
      roomId: activeRoomId,
      text: message,
      messageId: Date.now().toString(),
      files: uploadedFilesIds,
      images: uploadedImagesIds,
    });

    setMessage('');
    setUploadedImages([]);
    setUploadedFiles([]);
    setInputLoading(false);
    setUploadedFilesIds([]);
    setUploadedImagesIds([]);
  };

  const handleEmojiSelect = (emoji: Emoji) => {
    setMessage((prev) => prev + emoji.native);
  };

  return (
    <div className="flex flex-1 items-end justify-between gap-2 p-4 border rounded-lg border-light-border dark:border-dark-border bg-white">
      <div className="flex flex-col flex-1 gap-2">
        <MessageInput
          value={message}
          onChange={setMessage}
          disabled={isClosed || !canChat}
          onEnterPress={handleSend}
        />

        {uploadedFiles.length > 0 && (
          <div className="flex">
            <FilePreview
              files={uploadedFiles}
              onRemove={(uid) => {
                setUploadedFiles((prev) =>
                  prev.filter((file) => file.uid !== uid)
                );
              }}
            />
          </div>
        )}
        {uploadedImages.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <ImagePreview
              images={uploadedImages}
              onRemove={(uid) => {
                setUploadedImages((prev) =>
                  prev.filter((img) => img.uid !== uid)
                );
              }}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {canChat && (
          <>
            {isClosed ? (
              <Happyemoji className="cursor-not-allowed opacity-50 text-light-text-primary dark:text-dark-text-primary" />
            ) : (
              <Popover
                content={
                  <Picker
                    data={data}
                    onEmojiSelect={handleEmojiSelect}
                    theme="light"
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                }
                trigger="click"
                placement="topRight"
              >
                <Happyemoji className="cursor-pointer text-light-text-primary dark:text-dark-text-primary" />
              </Popover>
            )}

            {isClosed ? (
              <AttachCircle className="cursor-not-allowed opacity-50 text-light-text-primary dark:text-dark-text-primary" />
            ) : (
              <Popover
                content={
                  <div className="flex flex-col">
                    <FileUploadInput
                      roomId={activeRoomId}
                      setInputLoading={setInputLoading}
                      onFilesChange={setUploadedFiles}
                      onFileIdsChange={handleFileIdsChange}
                      isUploading={isUploading}
                      onUploadingChange={setIsUploading}
                      onRemove={(uid) => {
                        setUploadedFiles((prev) =>
                          prev.filter((file) => file.uid !== uid)
                        );
                      }}
                    />

                    <ImageUploadInput
                      roomId={activeRoomId}
                      setInputLoading={setInputLoading}
                      onImagesChange={setUploadedImages}
                      onImageIdsChange={handleImageIdsChange}
                      isUploading={isUploading}
                      onUploadingChange={setIsUploading}
                      onRemove={(uid) => {
                        setUploadedImages((prev) =>
                          prev.filter((img) => img.uid !== uid)
                        );
                      }}
                    />
                  </div>
                }
                trigger="click"
              >
                <AttachCircle className="cursor-pointer text-light-text-primary dark:text-dark-text-primary" />
              </Popover>
            )}
          </>
        )}

        <Button
          variant="primary"
          onClick={handleSend}
          icon={<Send size={35} />}
          disabled={
            !canChat ||
            isClosed ||
            (!message.trim() &&
              uploadedImages.length === 0 &&
              uploadedFiles.length === 0) ||
            inputLoading
          }
          className="flex gap-2"
        >
          <div className="flex gap-1">
            <span>{t('send')}</span>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default ChatFooter;
